import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import { ErrorService } from "@/lib/errors";
import { Cabinet } from "./types";
import type { Visibility } from "@hector/shared-types/resource-sharing";

/** Hook to fetch the current cabinet for the logged-in user. */
export const useCurrentCabinet = () => {
  const cabinetId = useCabinetId();

  return useQuery({
    queryKey: ["current-cabinet", cabinetId],
    queryFn: async () => {
      if (!cabinetId) return null;
      try {
        return await apiClient.get<Cabinet>(`/cabinets/${cabinetId}`);
      } catch (error) {
        ErrorService.handle(error instanceof Error ? error : new Error("Impossible de récupérer le cabinet"), { context: { action: 'load_cabinet' }, silent: true });
        return null;
      }
    },
    enabled: !!cabinetId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/** Hook to fetch all cabinets available for the user. */
export const useCabinets = () => {
  const cabinetId = useCabinetId();

  return useQuery({
    queryKey: ["cabinets", cabinetId],
    queryFn: async () => {
      if (!cabinetId) return [];
      try {
        const cabinet = await apiClient.get<Cabinet>(`/cabinets/${cabinetId}`);
        return cabinet ? [cabinet] : [];
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_cabinets' }, silent: true });
        // Relancer plutôt que renvoyer [] : un échec transitoire ne doit pas être mis
        // en cache 5 min comme « aucun cabinet » (nom du cabinet vide dans le menu et
        // les Paramètres), et doit bénéficier des retries du QueryClient.
        throw error;
      }
    },
    enabled: !!cabinetId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Enregistre le réglage cabinet « Partage des dossiers par défaut » (admin du cabinet
 * uniquement côté serveur). Invalide les deux caches de la fiche cabinet : les
 * Paramètres lisent `cabinets`, le dialogue « Nouveau dossier » lit `current-cabinet`.
 */
export const useUpdateDefaultCaseVisibility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cabinetId,
      default_case_visibility,
    }: {
      cabinetId: string;
      default_case_visibility: Visibility;
    }) => {
      return await apiClient.patch<{ default_case_visibility: Visibility }>(
        `/cabinets/${cabinetId}/default-case-visibility`,
        { default_case_visibility },
      );
    },
    onSuccess: (_data, { cabinetId }) => {
      queryClient.invalidateQueries({ queryKey: ["cabinets", cabinetId] });
      queryClient.invalidateQueries({ queryKey: ["cabinets"] });
      queryClient.invalidateQueries({ queryKey: ["current-cabinet", cabinetId] });
      queryClient.invalidateQueries({ queryKey: ["current-cabinet"] });
    },
  });
};

/**
 * Renomme le cabinet (admin du cabinet uniquement côté serveur). Mêmes caches que
 * le partage par défaut : `cabinets` porte le nom affiché dans les Paramètres et le
 * menu de l'avatar, `current-cabinet` la fiche lue ailleurs.
 */
export const useUpdateCabinetName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cabinetId, name }: { cabinetId: string; name: string }) => {
      return await apiClient.patch<{ name: string }>(`/cabinets/${cabinetId}/name`, { name });
    },
    onSuccess: (_data, { cabinetId }) => {
      queryClient.invalidateQueries({ queryKey: ["cabinets", cabinetId] });
      queryClient.invalidateQueries({ queryKey: ["cabinets"] });
      queryClient.invalidateQueries({ queryKey: ["current-cabinet", cabinetId] });
      queryClient.invalidateQueries({ queryKey: ["current-cabinet"] });
    },
  });
};
